// Which area each thing waiting for a person belongs in (E15, #367; docs/specs/MODULE_NAVIGATION.md "The inbox"; Home
// itself went on 30 Oct 2026, docs/specs/CAMPAIGN_LED_2026_10.md). One rule for the inbox, the town and (through
// engine/data/task_areas.json, kept in step by inbox.test.ts) the morning digest.
import { MODULE_DEFS } from "../modules";
import type { ModuleKey } from "./setupCatalogue";
import { TASK_HOMES } from "./nav";
import { AGENT_HOME, isAccountProblem } from "./townSignals";
import type { HumanTask } from "./types";
import type { OwnerTask } from "./ownerTasks";

export type AreaKey = ModuleKey | "crm" | "general";

export interface Area {
  key: AreaKey;
  label: string;
  /** The left menu's section key, for the panel's Go there (none for General). */
  section: string | null;
}

/**
 * The panels, always in this order: the engine's modules (Find's work shows as Campaigns, Connect's as Conversations),
 * then Contacts, then General. The keys stay the modules' (the digest and the agents use them).
 */
export const AREAS: Area[] = [
  { key: "attract", label: "Attract", section: "attract" },
  { key: "find", label: "Campaigns", section: "campaigns" },
  { key: "network", label: "Network", section: "network" },
  { key: "connect", label: "Conversations", section: "conversations" },
  { key: "reconnect", label: "ReConnect", section: "reconnect" },
  { key: "crm", label: "Contacts", section: "contacts" },
  { key: "general", label: "General", section: null },
];

export const AREA_BY_KEY = Object.fromEntries(AREAS.map((a) => [a.key, a])) as Record<AreaKey, Area>;

const AREA_OF_SECTION: Record<string, AreaKey> = {
  ...Object.fromEntries(AREAS.filter((a) => a.section).map((a) => [a.section!, a.key])),
  // Quotes (28 Oct 2026): quoting is part of selling, so its tasks wait in Conversations' panel.
  quotes: "connect",
  // Results' tasks are about searches running low: Find's work.
  results: "find",
};

/**
 * Kinds homed outside their area's place that belong to that area: the account, rule or record they are about is that
 * area's (29 Oct 2026: their pages moved to Settings, their panel did not).
 */
const SETTINGS_KIND_AREA: Record<string, AreaKey> = {
  contact_finder_problem: "find",
  call_back: "find",
  approve_whatsapp_templates: "connect",
  whatsapp_template_problem: "connect",
  reconnect_phone_line: "connect",
  notetaker_problem: "connect",
  payment_problem: "connect",
  autonomy_scope_promotion: "connect",
  review_duplicates: "crm",
  search_running_low: "find",
  // 30 Oct 2026: a tender waits beside the news in Contacts › Find, still Find's work; your own to-dos are done on
  // Conversations › Next steps but keep the area they were filed under (areaOfOwnerTask).
  review_tender: "find",
  owner_todo: "general",
  find_scoring_blocked: "find",
};

/** A kind's area: the first destination (menu order) whose registry lists it, else General. */
export function areaOfKind(kind: string): AreaKey {
  if (kind in SETTINGS_KIND_AREA) return SETTINGS_KIND_AREA[kind]!;
  const mod = MODULE_DEFS.find((m) => kind in m.taskHomes);
  return (mod && AREA_OF_SECTION[mod.key]) ?? "general";
}

/** The area of the module an agent works in; the strategy and ops agents (Settings) are General. */
export function areaOfAgent(agent: string): AreaKey {
  const b = AGENT_HOME[agent]?.building;
  return b && AREAS.some((a) => a.key === b) ? (b as AreaKey) : "general";
}

const FAILING = new Set(["engine_part_failing", "health_alert"]);

/** The area of a task waiting for a person: a failing part goes where its agent works. */
export function areaOf(t: Pick<HumanTask, "kind" | "payload">): AreaKey {
  const agent = t.payload?.agent;
  if (FAILING.has(t.kind) && typeof agent === "string" && agent in AGENT_HOME) return areaOfAgent(agent);
  return areaOfKind(t.kind);
}

/** The area of a to-do or next step: next steps are Connect's; a to-do about a record is CRM's, else General's. */
export function areaOfOwnerTask(t: Pick<OwnerTask, "source" | "person_id" | "organisation_id" | "deal_id">): AreaKey {
  if (t.source === "next_step") return "connect";
  return t.person_id || t.organisation_id || t.deal_id ? "crm" : "general";
}

/** A to-do or next step that waits now (due today or earlier); a later one sits in Coming up. */
export function isDue(t: Pick<OwnerTask, "bucket">): boolean {
  return t.bucket === "overdue" || t.bucket === "today";
}

/** A person's next step (crm__is_next_step_task): it comes with its day from crm_owner_tasks, so the queue skips it. */
export function isNextStep(t: Pick<HumanTask, "kind" | "entity_type" | "payload">): boolean {
  return t.kind === "follow_up" && t.entity_type === "person" && String(t.payload?.next_step ?? "") === "true";
}

/** The page an item's Go there opens: its first home in its own area, with ?task= so the card opens there. */
export function goThere(t: Pick<HumanTask, "id" | "kind" | "payload">): string | null {
  const area = areaOf(t);
  const section = AREAS.find((a) => a.key === area)?.section;
  if (FAILING.has(t.kind)) {
    const why = `${String(t.payload?.error ?? "")} ${String(t.payload?.note ?? "")} ${String(t.payload?.problem ?? "")}`;
    // An account that needs signing in again is fixed in Settings (Find's mailboxes, or the connections); anything else
    // on Settings › Automations.
    if (isAccountProblem(why) && (area === "find" || area === "connect")) return area === "find" ? "/settings/sending" : "/settings/connections";
    return "/settings/automations";
  }
  const homes = TASK_HOMES[t.kind] ?? [];
  const home = homes.find((h) => section && (h === `/${section}` || h.startsWith(`/${section}/`))) ?? homes[0];
  return home ? `${home}?task=${t.id}` : null;
}

/** What the digest reads (engine/data/task_areas.json): the panels in order, every kind's area and every agent's. */
export function taskAreasFile(): { areas: { key: AreaKey; label: string }[]; kinds: Record<string, AreaKey>; agents: Record<string, AreaKey> } {
  const areas = AREAS.map(({ key, label }) => ({ key, label }));
  const kinds = Object.fromEntries(Object.keys(TASK_HOMES).sort().map((k) => [k, areaOfKind(k)]));
  const agents = Object.fromEntries(Object.keys(AGENT_HOME).sort().map((a) => [a, areaOfAgent(a)]));
  return { areas, kinds, agents };
}
